'use client'

import { useState, useRef, useEffect } from 'react'
import { Checkbox } from '@/ui/checkbox'
import { Input } from '@/ui/input'
import { Button } from '@/ui/button'
import { XIcon, PlusIcon } from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import type { ChecklistItem } from './meta'

interface TaskChecklistProps {
  items: ChecklistItem[]
  onToggle?: (id: string, checked: boolean) => void
  disabled?: boolean
  className?: string
}

/**
 * Interactive checklist component for tasks.
 * Renders a list of checkbox items that can be toggled.
 */
export function TaskChecklist({
  items,
  onToggle,
  disabled = false,
  className,
}: TaskChecklistProps) {
  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className={cn('space-y-0.5 sm:space-y-2', className)}>
      {items.map((item) => (
        <div key={item.id} className="flex items-start gap-2">
          <Checkbox
            id={`checklist-${item.id}`}
            checked={item.checked}
            onCheckedChange={(checked) => {
              if (!disabled && onToggle) {
                onToggle(item.id, checked === true)
              }
            }}
            disabled={disabled}
          />
          <label
            htmlFor={`checklist-${item.id}`}
            className={cn(
              'cursor-pointer text-sm leading-[1.15] select-none',
              item.checked && 'text-muted-foreground line-through',
              disabled && 'cursor-default',
            )}
          >
            {item.text}
          </label>
        </div>
      ))}
    </div>
  )
}

/**
 * Read-only checklist display for collapsed views.
 * Shows a compact visual summary of completed items.
 */
export function TaskChecklistSummary({
  items,
  className,
}: {
  items: ChecklistItem[]
  className?: string
}) {
  if (!items || items.length === 0) {
    return null
  }

  const completedCount = items.filter((item) => item.checked).length
  const totalCount = items.length
  const allDone = completedCount === totalCount

  return (
    <span
      className={cn(
        'text-xs tabular-nums',
        allDone ? 'text-success' : 'text-muted-foreground',
        className,
      )}
    >
      {completedCount}/{totalCount}
    </span>
  )
}

interface EditableTaskChecklistProps {
  items: ChecklistItem[]
  onItemsChange: (items: ChecklistItem[]) => void
  onToggle?: (id: string, checked: boolean) => void
  disabled?: boolean
  className?: string
}

/**
 * Editable checklist component for tasks.
 * Click on text to edit inline, checkbox only toggles completion.
 * Allows adding and removing items.
 */
export function EditableTaskChecklist({
  items,
  onItemsChange,
  onToggle,
  disabled = false,
  className,
}: EditableTaskChecklistProps) {
  const [newItemText, setNewItemText] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)
  const newItemInputRef = useRef<HTMLInputElement>(null)

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingId])

  const handleAddItem = () => {
    if (!newItemText.trim() || disabled) return

    const newItem: ChecklistItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      text: newItemText.trim(),
      checked: false,
    }

    onItemsChange([...items, newItem])
    setNewItemText('')
    newItemInputRef.current?.focus()
  }

  const handleRemoveItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    onItemsChange(items.filter((item) => item.id !== id))
  }

  const handleStartEdit = (item: ChecklistItem, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    setEditingId(item.id)
    setEditText(item.text)
  }

  const handleSaveEdit = () => {
    if (!editingId) return

    if (!editText.trim()) {
      // If empty, remove the item
      onItemsChange(items.filter((item) => item.id !== editingId))
    } else {
      onItemsChange(
        items.map((item) =>
          item.id === editingId ? { ...item, text: editText.trim() } : item,
        ),
      )
    }
    setEditingId(null)
    setEditText('')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditText('')
  }

  const handleToggle = (id: string, checked: boolean) => {
    if (disabled) return
    if (onToggle) {
      onToggle(id, checked)
    } else {
      onItemsChange(
        items.map((item) => (item.id === id ? { ...item, checked } : item)),
      )
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, action: 'add' | 'edit') => {
    if (e.key === 'Enter') {
      e.preventDefault()
      action === 'add' ? handleAddItem() : handleSaveEdit()
    } else if (e.key === 'Escape') {
      action === 'edit' && handleCancelEdit()
    }
  }

  return (
    <div className={cn('space-y-0.5 sm:space-y-2', className)}>
      {items.map((item) => (
        <div key={item.id} className="group flex items-start gap-2">
          <Checkbox
            id={`checklist-edit-${item.id}`}
            checked={item.checked}
            onCheckedChange={(checked) =>
              handleToggle(item.id, checked === true)
            }
            disabled={disabled}
            className="shrink-0"
          />

          {editingId === item.id ? (
            <>
              <Input
                ref={editInputRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'edit')}
                onBlur={handleSaveEdit}
                className="h-6 flex-1 text-sm"
                disabled={disabled}
              />
              {/* Spacer to maintain alignment when editing */}
              <div className="h-5 w-5 shrink-0" />
            </>
          ) : (
            <>
              <span
                onClick={(e) => handleStartEdit(item, e)}
                className={cn(
                  'flex-1 cursor-text text-sm leading-[1.15]',
                  item.checked && 'text-muted-foreground line-through',
                  disabled && 'cursor-default',
                )}
              >
                {item.text}
              </span>

              {!disabled ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleRemoveItem(item.id, e)}
                  className="text-muted-foreground hover:text-destructive h-5 w-5 shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              ) : (
                // Spacer to maintain alignment when disabled
                <div className="h-5 w-5 shrink-0" />
              )}
            </>
          )}
        </div>
      ))}

      {/* Add new item */}
      {!disabled && (
        <div className="flex items-center gap-2 pt-1">
          <Input
            ref={newItemInputRef}
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'add')}
            placeholder="Add item..."
            className="h-6 flex-1 text-sm"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddItem}
            disabled={!newItemText.trim()}
            className="h-5 w-5 shrink-0 p-0"
          >
            <PlusIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Helper function to create checklist items from an array of strings.
 * Each item gets a unique ID and starts unchecked.
 */
export function createChecklistItems(texts: string[]): ChecklistItem[] {
  return texts.map((text, index) => ({
    id: `item-${index}-${Date.now()}`,
    text: text.trim(),
    checked: false,
  }))
}

/**
 * Helper function to toggle a checklist item's checked state.
 * Returns a new array with the updated item.
 */
export function toggleChecklistItem(
  items: ChecklistItem[],
  id: string,
  checked: boolean,
): ChecklistItem[] {
  return items.map((item) => (item.id === id ? { ...item, checked } : item))
}
